import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import * as authApi from '../api/auth.js';

// Define the token key to match other files
const TOKEN_KEY = 'moftah_auth_token';

// Define types
interface User {
  isAuthenticated: boolean;
  username?: string;
  role?: string;
  id?: number;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  register: (username: string, password: string, role: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Create context with default values
const AuthContext = createContext<AuthContextType | null>(null);

// Define the provider component
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is already logged in (token exists)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (authApi.isAuthenticated()) {
          // Get the token from localStorage
          const token = localStorage.getItem(TOKEN_KEY);
          
          if (token) {
            // Decode JWT token to get user info - in a real app, you'd verify this on the server
            try {
              // Basic JWT parsing (token is in format header.payload.signature)
              const tokenParts = token.split('.');
              if (tokenParts.length === 3) {
                const payload = JSON.parse(atob(tokenParts[1]));
                
                // Set user information from token
                setUser({
                  isAuthenticated: true,
                  username: payload.username,
                  role: payload.role,
                  id: payload.userId
                });
              } else {
                throw new Error('Invalid token format');
              }
            } catch (err) {
              console.error('Error decoding token:', err);
              authApi.logout();
            }
          }
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        // If token is invalid, clear it
        authApi.logout();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (username: string, password: string): Promise<{ success: boolean; message?: string }> => {
    setLoading(true);
    try {
      const response = await authApi.login(username, password);
      
      // Set user information
      setUser({
        isAuthenticated: true,
        username: response.user.username,
        role: response.user.role,
        id: response.user.id
      });
      
      // Redirect to dashboard
      navigate('/dashboard');
      return { success: true };
    } catch (error: any) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (username: string, password: string, role: string): Promise<{ success: boolean; message?: string }> => {
    setLoading(true);
    try {
      await authApi.register(username, password, role);
      return { success: true };
    } catch (error: any) {
      console.error('Registration failed:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Registration failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    authApi.logout();
    setUser(null);
    navigate('/login');
  };

  // Values to be provided to consuming components
  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 